import React, { useEffect, useMemo, useRef, useState } from "react";
import translations from "../i18n/homepage";
import "./HomePage.css";

const LANG_KEY = "meiki:lang";
const SUPPORTED = ["jp", "zh", "en"];

/* ─── Imagery (Unsplash CDN, free for commercial use) ──────────────── */
const UNSPLASH = "https://images.unsplash.com";
const UNSPLASH_PARAMS = "auto=format&fit=crop&q=72";

const IMAGES = {
  hero: {
    src: `${UNSPLASH}/photo-1741097574041-d70d3fe6a3ab?w=2000&${UNSPLASH_PARAMS}`,
    srcSetSmall: `${UNSPLASH}/photo-1741097574041-d70d3fe6a3ab?w=900&${UNSPLASH_PARAMS}`,
    alt: "",
  },
  realEstate: {
    src: `${UNSPLASH}/photo-1678481940372-205d3765b34f?w=1200&${UNSPLASH_PARAMS}`,
    alt: "",
  },
  education: {
    src: `${UNSPLASH}/photo-1713018604446-198b165aa772?w=1200&${UNSPLASH_PARAMS}`,
    alt: "",
  },
};

const PHOTO_CREDITS = [
  { name: "Wenhao Ruan", url: "https://unsplash.com/@wenhao_ruan" },
  { name: "pcYN7Pi8", url: "https://unsplash.com/" },
  { name: "XS Xue", url: "https://unsplash.com/@kngstm" },
];

function getInitialLang() {
  if (typeof window === "undefined") return "jp";
  try {
    const saved = window.localStorage.getItem(LANG_KEY);
    if (saved && SUPPORTED.includes(saved)) return saved;
  } catch (_) {
    /* ignore */
  }
  const nav = (window.navigator.language || "").toLowerCase();
  if (nav.startsWith("zh")) return "zh";
  if (nav.startsWith("en")) return "en";
  return "jp";
}

/**
 * Premium homepage for 鳴奇株式会社 (MEIKI Co., Ltd.)
 * - Tri-lingual (JP / ZH / EN)
 * - Dark navy + warm gold theme
 * - Rich entrance, scroll-reveal and hover animations
 */
export default function HomePage() {
  const [lang, setLang] = useState(getInitialLang);
  const [scrolled, setScrolled] = useState(false);
  const rootRef = useRef(null);

  const t = translations[lang];

  useEffect(() => {
    try {
      window.localStorage.setItem(LANG_KEY, lang);
    } catch (_) {
      /* ignore */
    }
    document.documentElement.lang =
      lang === "jp" ? "ja" : lang === "zh" ? "zh" : "en";
  }, [lang]);

  useEffect(() => {
    document.title =
      lang === "zh"
        ? "鸣奇株式会社 | MEIKI 不动产 · 留学 · 跨境服务"
        : lang === "en"
        ? "MEIKI Co., Ltd. | Real Estate, Education & Cross-border Services"
        : "鳴奇株式会社 | MEIKI 不動産・留学・クロスボーダーサービス";
  }, [lang]);

  /* Scrolled-state for the nav bar */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* IntersectionObserver — reveal-on-scroll */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const items = root.querySelectorAll(".reveal");
    if (!("IntersectionObserver" in window)) {
      items.forEach((el) => el.classList.add("is-visible"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -60px 0px" }
    );
    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [lang]);

  /* Service-card mouse glow follow */
  const handleCardMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    e.currentTarget.style.setProperty("--mx", `${x}%`);
    e.currentTarget.style.setProperty("--my", `${y}%`);
  };

  /* Smooth-scroll handler for in-page anchors */
  const scrollToId = (id) => (e) => {
    if (e) e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const marqueeItems = useMemo(() => {
    return [...t.marquee, ...t.marquee, ...t.marquee];
  }, [t.marquee]);

  return (
    <div className="meiki-root" ref={rootRef}>
      <div className="meiki-bg" aria-hidden />
      <div className="meiki-grid" aria-hidden />

      <div className="meiki-shell">
        {/* ----- Nav ----- */}
        <header className={`meiki-nav ${scrolled ? "is-scrolled" : ""}`}>
          <a
            className="meiki-brand"
            href="#top"
            onClick={scrollToId("top")}
            aria-label="MEIKI"
          >
            <span className="brand-dot" aria-hidden />
            MEIKI
            <span className="brand-sub">Real Estate Services</span>
          </a>

          <nav className="meiki-nav-links" aria-label="Primary">
            <a href="#about" onClick={scrollToId("about")}>
              {t.nav.about}
            </a>
            <a href="#services" onClick={scrollToId("services")}>
              {t.nav.services}
            </a>
            <a href="#vision" onClick={scrollToId("vision")}>
              {t.nav.vision}
            </a>
            <a href="#contact" onClick={scrollToId("contact")}>
              {t.nav.contact}
            </a>
          </nav>

          <div className="meiki-nav-right">
            <div
              className="meiki-lang"
              role="group"
              aria-label="Language switcher"
            >
              {SUPPORTED.map((code) => (
                <button
                  key={code}
                  type="button"
                  className={lang === code ? "is-active" : ""}
                  onClick={() => setLang(code)}
                  aria-pressed={lang === code}
                >
                  {code === "jp" ? "JP" : code === "zh" ? "中" : "EN"}
                </button>
              ))}
            </div>
            <button
              className="meiki-cta-btn"
              type="button"
              onClick={scrollToId("contact")}
            >
              <span>{t.nav.cta}</span>
            </button>
          </div>
        </header>

        {/* ----- Hero ----- */}
        <section
          className="meiki-hero"
          id="top"
          aria-labelledby="meiki-hero-title"
        >
          <div className="meiki-hero-media" aria-hidden>
            <img
              src={IMAGES.hero.src}
              srcSet={`${IMAGES.hero.srcSetSmall} 900w, ${IMAGES.hero.src} 2000w`}
              sizes="100vw"
              alt={IMAGES.hero.alt}
              loading="eager"
              fetchpriority="high"
              decoding="async"
            />
            <div className="meiki-hero-overlay" />
            <div className="meiki-hero-grain" />
          </div>

          <div className="meiki-container">
            <div className="meiki-hero-eyebrow meiki-eyebrow">
              {t.hero.eyebrow}
            </div>
            <h1 id="meiki-hero-title" key={`hero-${lang}`}>
              {t.hero.titleLines.map((line, i) => (
                <span className="word-line" key={i}>
                  <span>{line}</span>
                </span>
              ))}
            </h1>
            <div className="meiki-hero-subtitle">— {t.hero.subtitle}</div>
            <p className="meiki-hero-desc">{t.hero.description}</p>
            <div className="meiki-hero-actions">
              <button
                className="meiki-btn-primary"
                type="button"
                onClick={scrollToId("services")}
              >
                {t.hero.primary}
                <span className="meiki-arrow" aria-hidden>
                  →
                </span>
              </button>
              <button
                className="meiki-btn-ghost"
                type="button"
                onClick={scrollToId("contact")}
              >
                {t.hero.secondary}
                <span className="meiki-arrow" aria-hidden>
                  →
                </span>
              </button>
            </div>
          </div>

          <div className="meiki-hero-meta" aria-hidden>
            <span className="pulse" />
            TOKYO · JAPAN
          </div>

          <div className="meiki-scroll-hint" aria-hidden>
            scroll
          </div>
        </section>

        {/* ----- Marquee ----- */}
        <div className="meiki-marquee" aria-hidden>
          <div className="meiki-marquee-track">
            {marqueeItems.map((item, i) => (
              <span key={`${item}-${i}`}>{item}</span>
            ))}
          </div>
        </div>

        {/* ----- About ----- */}
        <section className="meiki-section" id="about">
          <div className="meiki-container">
            <div className="meiki-section-head">
              <div className="meiki-eyebrow reveal">{t.about.eyebrow}</div>
              <h2 className="reveal" style={{ "--reveal-delay": "120ms" }}>
                {t.about.title}
              </h2>
            </div>

            <div className="meiki-about-grid">
              <div className="meiki-about-text">
                {t.about.paragraphs.map((p, i) => (
                  <p
                    key={i}
                    className="reveal"
                    style={{ "--reveal-delay": `${i * 120}ms` }}
                  >
                    {p}
                  </p>
                ))}
              </div>
              <dl className="meiki-stats reveal">
                {t.about.stats.map((s, i) => (
                  <div className="meiki-stat" key={i}>
                    <dt className="meiki-stat-value">{s.value}</dt>
                    <dd className="meiki-stat-label">{s.label}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </section>

        {/* ----- Services ----- */}
        <section className="meiki-section meiki-services" id="services">
          <div className="meiki-container">
            <div className="meiki-section-head">
              <div className="meiki-eyebrow reveal">{t.services.eyebrow}</div>
              <h2 className="reveal" style={{ "--reveal-delay": "120ms" }}>
                {t.services.title}
              </h2>
            </div>

            <div className="meiki-services-grid">
              {t.services.items.map((item, i) => {
                const cover = i === 0 ? IMAGES.realEstate : IMAGES.education;
                return (
                  <article
                    key={i}
                    className="meiki-service-card reveal"
                    style={{ "--reveal-delay": `${i * 160}ms` }}
                    onMouseMove={handleCardMouseMove}
                  >
                    <div className="meiki-service-media">
                      <img
                        src={cover.src}
                        alt={cover.alt}
                        loading="lazy"
                        decoding="async"
                      />
                      <span className="meiki-service-media-tag">
                        0{i + 1} / {t.services.items.length.toString().padStart(2, "0")}
                      </span>
                    </div>
                    <div className="meiki-service-body">
                      <div className="meiki-service-num">{item.number}</div>
                      <div className="meiki-service-tag">{item.tag}</div>
                      <h3 className="meiki-service-title">{item.title}</h3>
                      <p className="meiki-service-desc">{item.description}</p>
                      <ul className="meiki-service-list">
                        {item.highlights.map((h, j) => (
                          <li key={j}>{h}</li>
                        ))}
                      </ul>
                      <div className="meiki-service-bottom">
                        <span>{item.tag}</span>
                        <span aria-hidden>—</span>
                        <span>0{i + 1}</span>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* ----- Vision ----- */}
        <section className="meiki-section" id="vision">
          <div className="meiki-container">
            <div className="meiki-section-head">
              <div className="meiki-eyebrow reveal">{t.vision.eyebrow}</div>
              <h2 className="reveal" style={{ "--reveal-delay": "120ms" }}>
                {t.vision.title}
              </h2>
            </div>

            <p
              className="meiki-vision-body reveal"
              style={{ "--reveal-delay": "120ms" }}
            >
              {t.vision.body}
            </p>

            <div className="meiki-pillars">
              {t.vision.pillars.map((p, i) => (
                <div
                  key={i}
                  className="meiki-pillar reveal"
                  style={{ "--reveal-delay": `${i * 140}ms` }}
                >
                  <div className="meiki-pillar-title">{p.title}</div>
                  <p className="meiki-pillar-desc">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ----- Contact ----- */}
        <section className="meiki-section meiki-contact" id="contact">
          <div className="meiki-container">
            <div className="meiki-section-head">
              <div className="meiki-eyebrow reveal">{t.contact.eyebrow}</div>
              <h2 className="reveal" style={{ "--reveal-delay": "120ms" }}>
                {t.contact.title}
              </h2>
            </div>

            <div className="meiki-contact-grid">
              <div>
                <p
                  className="meiki-contact-desc reveal"
                  style={{ "--reveal-delay": "60ms" }}
                >
                  {t.contact.description}
                </p>
                <dl className="meiki-info reveal">
                  <div className="meiki-info-row">
                    <dt>Address</dt>
                    <dd>{t.contact.address}</dd>
                  </div>
                  <div className="meiki-info-row">
                    <dt>Tel</dt>
                    <dd>
                      <a href={`tel:${t.contact.tel.replace(/[^0-9+]/g, "")}`}>
                        {t.contact.tel}
                      </a>
                    </dd>
                  </div>
                  <div className="meiki-info-row">
                    <dt>Fax</dt>
                    <dd>{t.contact.fax}</dd>
                  </div>
                  <div className="meiki-info-row">
                    <dt>License</dt>
                    <dd>{t.contact.license}</dd>
                  </div>
                </dl>
              </div>

              <div className="meiki-contact-actions">
                <a
                  href="/line"
                  className="meiki-action-card reveal"
                  style={{ "--reveal-delay": "80ms" }}
                >
                  <div>
                    <span className="label">LINE</span>
                    <span className="value">{t.contact.lineCta}</span>
                  </div>
                  <span className="arrow-circle" aria-hidden>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 11L11 3M11 3H4.5M11 3V9.5"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </a>
                <a
                  href="mailto:info@meiki.example"
                  className="meiki-action-card reveal"
                  style={{ "--reveal-delay": "180ms" }}
                >
                  <div>
                    <span className="label">Email</span>
                    <span className="value">{t.contact.emailCta}</span>
                  </div>
                  <span className="arrow-circle" aria-hidden>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 11L11 3M11 3H4.5M11 3V9.5"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ----- Footer ----- */}
        <footer className="meiki-footer">
          <div className="meiki-container meiki-footer-inner">
            <div className="meiki-footer-tag">{t.footer.tagline}</div>
            <div className="meiki-footer-copy">{t.footer.copyright}</div>
          </div>
          <div className="meiki-container meiki-footer-credits">
            <span>Photography</span>
            {PHOTO_CREDITS.map((p, i) => (
              <React.Fragment key={p.name}>
                {i > 0 && <span aria-hidden>·</span>}
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="meiki-credit-link"
                >
                  {p.name}
                </a>
              </React.Fragment>
            ))}
            <span aria-hidden>·</span>
            <a
              href="https://unsplash.com"
              target="_blank"
              rel="noopener noreferrer"
              className="meiki-credit-link"
            >
              Unsplash
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
}
